import { useEffect, useState, useCallback } from "react";
import { Show, useAuth } from "@clerk/react";
import { Redirect, useRoute, useLocation } from "wouter";
import { Loader2, MessageSquare, FileText, History, Scale } from "lucide-react";
import { GuestLayout } from "@/components/GuestLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

interface GuestSession {
  contractId: number;
  orgName: string | null;
  contractLabel: string | null;
  expiresAt: string;
}
interface GuestClause {
  id: number;
  clauseId: number;
  position: number;
  title: string;
  body: string;
}
interface GuestContract {
  id: number;
  coachName: string;
  sport: string;
  contractType: string;
  startDate: string;
  endDate: string;
  annualSalary: number;
  status: string;
  notes: string | null;
  clauses: GuestClause[];
}
interface GuestComment {
  id: number;
  authorName: string;
  body: string;
  createdAt: string;
  isRedline: boolean;
  canEdit: boolean;
}
interface GuestAuditEntry {
  id: number;
  action: string;
  field: string | null;
  oldValue: string | null;
  newValue: string | null;
  actorName: string | null;
  actorPersonas: string[];
  actorIsGuest: boolean;
  createdAt: string;
}

export default function GuestContractPage() {
  return (
    <>
      <Show when="signed-out">
        <Redirect to="/sign-in" />
      </Show>
      <Show when="signed-in">
        <GuestContractInner />
      </Show>
    </>
  );
}

function GuestContractInner() {
  const [, params] = useRoute("/guest/contracts/:id");
  const [, setLocation] = useLocation();
  const { getToken } = useAuth();
  const contractIdParam = Number(params?.id);

  const [session, setSession] = useState<GuestSession | null>(null);
  const [contract, setContract] = useState<GuestContract | null>(null);
  const [comments, setComments] = useState<GuestComment[]>([]);
  const [audit, setAudit] = useState<GuestAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [redlineClauseId, setRedlineClauseId] = useState<number | "">("");
  const [posting, setPosting] = useState(false);

  const authedFetch = useCallback(
    async (path: string, init?: RequestInit): Promise<Response> => {
      const token = await getToken();
      const headers: Record<string, string> = {
        ...(init?.headers as Record<string, string> | undefined),
      };
      if (token) headers.Authorization = `Bearer ${token}`;
      if (init?.body) headers["Content-Type"] ??= "application/json";
      return fetch(path, { ...init, headers, credentials: "include" });
    },
    [getToken],
  );

  const reload = useCallback(async () => {
    if (!Number.isInteger(contractIdParam) || contractIdParam <= 0) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      const [meRes, cRes, commRes, audRes] = await Promise.all([
        authedFetch("/api/guest/me"),
        authedFetch(`/api/guest/contracts/${contractIdParam}`),
        authedFetch(`/api/guest/contracts/${contractIdParam}/comments`),
        authedFetch(`/api/guest/contracts/${contractIdParam}/audit`),
      ]);
      if (meRes.status === 401) {
        setLocation("/sign-in");
        return;
      }
      if (meRes.status === 403) {
        setErrorMsg(
          "Your guest reviewer access is not active. The invitation may have expired or been revoked.",
        );
        return;
      }
      const me = (await meRes.json()) as GuestSession & { contractId: number };
      setSession(me);
      if (me.contractId !== contractIdParam) {
        setLocation(`/guest/contracts/${me.contractId}`);
        return;
      }
      if (!cRes.ok) {
        setErrorMsg("Could not load contract.");
        return;
      }
      setContract((await cRes.json()) as GuestContract);
      if (commRes.ok) setComments((await commRes.json()) as GuestComment[]);
      if (audRes.ok) setAudit((await audRes.json()) as GuestAuditEntry[]);
    } finally {
      setLoading(false);
    }
  }, [contractIdParam, authedFetch, setLocation]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function postComment(): Promise<void> {
    if (!draft.trim() || posting) return;
    setPosting(true);
    try {
      const res = await authedFetch(`/api/guest/contracts/${contractIdParam}/comments`, {
        method: "POST",
        body: JSON.stringify({
          body: draft,
          redlineClauseId: redlineClauseId === "" ? undefined : redlineClauseId,
        }),
      });
      if (res.ok) {
        setDraft("");
        setRedlineClauseId("");
        await reload();
      }
    } finally {
      setPosting(false);
    }
  }

  if (loading && !session) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (errorMsg) {
    return (
      <GuestLayout
        orgName={session?.orgName ?? null}
        contractLabel={session?.contractLabel ?? null}
        expiresAt={session?.expiresAt ?? null}
      >
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            {errorMsg}
          </CardContent>
        </Card>
      </GuestLayout>
    );
  }

  return (
    <GuestLayout
      orgName={session?.orgName ?? null}
      // Operator-precedence fix: `??` binds tighter than the ternary, so the
      // original `session?.contractLabel ?? contract ? ... : null` resolved
      // as `(session?.contractLabel ?? contract) ? ... : null` — which made
      // the ternary truthy whenever contract was loaded, even when a
      // session label existed (and then rendered the contract label instead
      // of the session one). Parenthesizing the fallback restores the
      // intended "prefer session label, fall back to derived label, else null".
      contractLabel={
        session?.contractLabel ??
        (contract ? `${contract.coachName} — Contract #${contract.id}` : null)
      }
      expiresAt={session?.expiresAt ?? null}
    >
      <Tabs defaultValue="document" className="w-full">
        <TabsList>
          <TabsTrigger value="document" data-testid="tab-document">
            <FileText className="h-4 w-4 mr-1.5" />
            Document
          </TabsTrigger>
          <TabsTrigger value="comments" data-testid="tab-comments">
            <MessageSquare className="h-4 w-4 mr-1.5" />
            Comments & Redlines
          </TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">
            <History className="h-4 w-4 mr-1.5" />
            Activity
          </TabsTrigger>
        </TabsList>

        <TabsContent value="document" className="mt-4 space-y-4">
          {contract ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{contract.coachName} — {contract.sport}</CardTitle>
                </CardHeader>
                <CardContent className="text-sm grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div><div className="text-muted-foreground text-xs">Type</div><div>{contract.contractType}</div></div>
                  <div><div className="text-muted-foreground text-xs">Start</div><div>{contract.startDate}</div></div>
                  <div><div className="text-muted-foreground text-xs">End</div><div>{contract.endDate}</div></div>
                  <div><div className="text-muted-foreground text-xs">Status</div><div>{contract.status}</div></div>
                  <div className="col-span-2 sm:col-span-2">
                    <div className="text-muted-foreground text-xs">Annual salary</div>
                    <div className="font-mono">${contract.annualSalary.toLocaleString()}</div>
                  </div>
                </CardContent>
              </Card>
              {contract.clauses.map((c) => (
                <Card key={c.id} data-testid={`clause-${c.clauseId}`}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Badge variant="outline">§{c.position + 1}</Badge>
                      {c.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="prose prose-sm max-w-none whitespace-pre-wrap">{c.body}</div>
                    <div className="mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setRedlineClauseId(c.id);
                          const el = document.querySelector("[data-testid='tab-comments']") as HTMLButtonElement | null;
                          el?.click();
                        }}
                        data-testid={`redline-${c.clauseId}`}
                      >
                        <Scale className="h-3 w-3 mr-1.5" />
                        Propose redline
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          ) : null}
        </TabsContent>

        <TabsContent value="comments" className="mt-4 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                {redlineClauseId === "" ? "Add a comment" : `Redline on §${(contract?.clauses.find(x => x.id === redlineClauseId)?.position ?? 0) + 1}`}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Share your legal review or proposed change…"
                rows={4}
                data-testid="comment-input"
              />
              <div className="flex items-center gap-2 justify-end">
                {redlineClauseId !== "" ? (
                  <Button variant="ghost" size="sm" onClick={() => setRedlineClauseId("")}>
                    Clear redline target
                  </Button>
                ) : null}
                <Button onClick={postComment} disabled={!draft.trim() || posting} data-testid="comment-submit">
                  {posting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                  {redlineClauseId === "" ? "Post comment" : "Submit redline"}
                </Button>
              </div>
            </CardContent>
          </Card>
          {comments.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No comments or redlines yet.
            </p>
          ) : (
            comments.map((c) => (
              <Card key={c.id} data-testid={`comment-${c.id}`}>
                <CardContent className="py-3 space-y-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="font-medium text-foreground">{c.authorName}</span>
                    {c.isRedline ? <Badge variant="secondary">Redline</Badge> : null}
                    <span>· {new Date(c.createdAt).toLocaleString()}</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{c.body}</div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="audit" className="mt-4 space-y-2">
          {audit.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No activity yet.</p>
          ) : (
            audit.map((a) => (
              <Card key={a.id} data-testid={`audit-${a.id}`}>
                <CardContent className="py-3 text-sm flex items-start gap-3">
                  <div className="flex-1">
                    <div className="text-xs text-muted-foreground">
                      {new Date(a.createdAt).toLocaleString()}
                    </div>
                    <div>
                      <span className="font-medium">{a.actorName ?? "System"}</span>
                      {a.actorIsGuest ? <Badge variant="outline" className="ml-2">Guest</Badge> : null}
                      {a.actorPersonas.length > 0 ? (
                        <Badge variant="secondary" className="ml-2">{a.actorPersonas.join(", ")}</Badge>
                      ) : null}
                      <span className="text-muted-foreground"> · {a.action}{a.field ? ` (${a.field})` : ""}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </GuestLayout>
  );
}
